/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    AviPermissionResource,
    IAlertSyslogServer,
} from 'generated-types';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';
import { AlertSyslogConfig } from 'object-types';
import * as globalL10n from 'global-l10n';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { RepeatedMessageItem } from 'repeated-message-item';
import { AlertSyslogServerConfigItem } from 'message-items/alert-syslog-server.config-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

const { ...globalL10nKeys } = globalL10n;

const SYSLOG_SERVERS = 'syslog_servers';

type AlertSyslogConfigPartial = Omit<IAlertSyslogServer, 'syslog_servers'>;

/**
 * AlertSyslogConfig interface extending IAlertSyslogServer type.
 */
interface IAlertSyslogConfig extends AlertSyslogConfigPartial {
    syslog_servers?: RepeatedMessageItem<AlertSyslogServerConfigItem>
}

/**
 * IAlertSyslogData interface for over-riding data.
 */
interface IAlertSyslogData {
    config: IAlertSyslogConfig;
}

/**
 * @description AlertEmailConfig item.
 * @author Guru Prasad H K, alextsg
 */
export class AlertSyslogConfigItem extends withEditChildMessageItemMixin(
    withFullModalMixin(ObjectTypeItem),
) {
    public static ngDependencies = [L10nService];

    /**
     * alert syslog data.
     */
    public data: IAlertSyslogData;

    /**
     * l10nService instance to register source bundles and get keys from source bundles.
     */
    private readonly l10nService: L10nService;

    constructor(args: {}) {
        const extendedArgs = {
            objectName: 'alertsyslogconfig',
            objectType: AlertSyslogConfig,
            permissionName: AviPermissionResource.PERMISSION_ALERTSYSLOGCONFIG,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getNgDependency(L10nService);
    }

    /**
     * To get Syslog Servers from Syslog Server config.
     */
    public get syslogServers(): RepeatedMessageItem<AlertSyslogServerConfigItem> {
        const { syslog_servers: syslogServers } = this.config;

        return syslogServers;
    }
    /**
     * Method to add Syslog server item.
     */
    public addSyslogServer(): void {
        this.addChildMessageItem({
            field: SYSLOG_SERVERS,
        });
    }

    /**
     * Method to edit Syslog server server item.
     */
    public editSyslogServer(syslogServer: AlertSyslogServerConfigItem): void {
        this.addChildMessageItem({
            field: SYSLOG_SERVERS,
            messageItem: syslogServer,
        });
    }

    /** @override */
    public getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(globalL10nKeys.syslogLabel);
    }

    /**
     * Method used to import lazy-loaded modal component.
     */
    /* eslint-disable-next-line class-methods-use-this */
    /** @override */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            AlertSyslogConfigModalComponent,
        } = await import(
            /* webpackChunkName: "alert-syslog-config-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/alert-syslog-config-modal/alert-syslog-config-modal.component'
        );

        return AlertSyslogConfigModalComponent as Type<Component>;
    }
}
